import { supabase, isSupabaseConfigured, resolveFunctionError } from '@/lib/supabase';

// =============================================================
// Meta Pixel + API de Conversiones (CAPI)
// =============================================================
// La configuración vive en la tabla `gateways_config` (RLS solo-Admin). La
// tienda pública lee el Pixel ID / Test Event Code / estado a través de la vista
// `gateways_config_public`, que NUNCA expone el Access Token de CAPI. Ese token
// solo lo usa la Edge Function `meta-capi` (servidor-a-servidor).

export interface MetaPublicConfig {
  pixelId: string;
  testEventCode: string;
  enabled: boolean;
  configured: boolean;
}

export interface MetaAdminConfig {
  pixelId: string;
  capiAccessToken: string;
  testEventCode: string;
  enabled: boolean;
}

const EMPTY_PUBLIC: MetaPublicConfig = {
  pixelId: '',
  testEventCode: '',
  enabled: false,
  configured: false,
};

// Configuración pública del Pixel (segura para el navegador de la tienda).
export async function getMetaPublicConfig(): Promise<MetaPublicConfig> {
  if (!isSupabaseConfigured || !supabase) return { ...EMPTY_PUBLIC };
  try {
    const { data, error } = await supabase
      .from('gateways_config_public')
      .select('meta_pixel_id, meta_test_event_code, meta_pixel_enabled')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...EMPTY_PUBLIC };
    const row = data as Record<string, unknown>;
    const pixelId = String(row.meta_pixel_id ?? '').trim();
    const testEventCode = String(row.meta_test_event_code ?? '').trim();
    const enabled =
      row.meta_pixel_enabled === undefined || row.meta_pixel_enabled === null
        ? false
        : Boolean(row.meta_pixel_enabled);
    return {
      pixelId,
      testEventCode,
      enabled,
      configured: Boolean(pixelId) && enabled,
    };
  } catch (err) {
    console.warn('[metaPixel] getMetaPublicConfig failed:', err);
    return { ...EMPTY_PUBLIC };
  }
}

// ⚠️ SOLO ADMINISTRADOR: lee la configuración completa (incluido el token de
// CAPI) para el panel. La protección real la aplica RLS (is_admin()).
export async function fetchMetaAdminConfig(): Promise<MetaAdminConfig> {
  const empty: MetaAdminConfig = {
    pixelId: '',
    capiAccessToken: '',
    testEventCode: '',
    enabled: false,
  };
  if (!isSupabaseConfigured || !supabase) return empty;
  const { data, error } = await supabase
    .from('gateways_config')
    .select('meta_pixel_id, meta_capi_access_token, meta_test_event_code, meta_pixel_enabled')
    .limit(1)
    .maybeSingle();
  if (error || !data) return empty;
  const row = data as Record<string, unknown>;
  return {
    pixelId: String(row.meta_pixel_id ?? ''),
    capiAccessToken: String(row.meta_capi_access_token ?? ''),
    testEventCode: String(row.meta_test_event_code ?? ''),
    enabled:
      row.meta_pixel_enabled === undefined || row.meta_pixel_enabled === null
        ? false
        : Boolean(row.meta_pixel_enabled),
  };
}

// Guarda (upsert) la configuración de Meta desde el panel de administración.
export async function saveMetaAdminConfig(patch: Partial<MetaAdminConfig>): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase no está configurado');
  }
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.pixelId !== undefined) row.meta_pixel_id = patch.pixelId.trim();
  if (patch.capiAccessToken !== undefined) row.meta_capi_access_token = patch.capiAccessToken.trim();
  if (patch.testEventCode !== undefined) row.meta_test_event_code = patch.testEventCode.trim();
  if (patch.enabled !== undefined) row.meta_pixel_enabled = patch.enabled;

  const { data: existing } = await supabase
    .from('gateways_config')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase.from('gateways_config').update(row).eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from('gateways_config').insert(row);
    if (error) throw new Error(error.message);
  }
}

// -------------------------------------------------------------
// Envío de eventos a la API de Conversiones (servidor-a-servidor)
// -------------------------------------------------------------

export interface MetaCapiUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  department?: string;
  country?: string;
}

export interface MetaCapiContent {
  id: string;
  quantity: number;
  item_price: number;
  title?: string;
}

export interface MetaCapiEventInput {
  eventName: string;
  eventId: string;
  orderNumber?: string;
  orderId?: string;
  eventSourceUrl?: string;
  currency?: string;
  value?: number;
  contents?: MetaCapiContent[];
  numItems?: number;
  userData?: MetaCapiUserData;
}

// Lee una cookie del navegador (se usa para las cookies de atribución _fbp/_fbc).
export function readBrowserCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

// Envía un evento a Meta vía la Edge Function `meta-capi`. Los datos de usuario
// (email, teléfono, nombre...) se normalizan y hashean (SHA-256) en el servidor.
// Nunca lanza: es best-effort para no romper el flujo de compra del cliente.
export async function sendMetaCapiEvent(input: MetaCapiEventInput): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  try {
    const { error } = await supabase.functions.invoke('meta-capi', {
      body: {
        eventName: input.eventName,
        eventId: input.eventId,
        orderNumber: input.orderNumber,
        orderId: input.orderId,
        eventSourceUrl: input.eventSourceUrl ?? (typeof window !== 'undefined' ? window.location.href : ''),
        currency: input.currency ?? 'COP',
        value: input.value,
        contents: input.contents,
        numItems: input.numItems,
        userData: input.userData,
        fbp: readBrowserCookie('_fbp'),
        fbc: readBrowserCookie('_fbc'),
      },
    });
    if (error) {
      console.warn('[metaPixel] CAPI invoke error:', await resolveFunctionError(error));
    }
  } catch (err) {
    console.warn('[metaPixel] CAPI send failed:', err);
  }
}

// -------------------------------------------------------------
// Registro local de eventos (panel "Embudo en vivo")
// -------------------------------------------------------------
// Cada evento que se dispara al Pixel se guarda también en nuestra propia tabla
// para poder mostrar los conteos del embudo en el panel admin. Es best-effort:
// si falla, nunca rompe la navegación ni el flujo de compra del cliente.
export async function logMetaEvent(input: {
  eventName: string;
  eventId?: string;
  value?: number;
  currency?: string;
}): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;
  if (!input.eventName) return;
  try {
    const { error } = await supabase.from('meta_pixel_events').insert({
      event_name: input.eventName,
      event_id: input.eventId ?? null,
      source_url: typeof window !== 'undefined' ? window.location.href : null,
      value: typeof input.value === 'number' && Number.isFinite(input.value) ? input.value : null,
      currency: input.currency ?? null,
    });
    if (error) console.warn('[metaPixel] logMetaEvent error:', error.message);
  } catch (err) {
    console.warn('[metaPixel] logMetaEvent failed:', err);
  }
}

export interface MetaFunnelRow {
  eventName: string;
  day: string;
  total: number;
  totalValue: number;
}

// Lee los conteos diarios del embudo (últimos N días) desde la RPC, incluyendo
// el valor total vendido (COP) del evento Purchase. RLS protege la lectura:
// solo un administrador obtiene filas.
export async function fetchMetaFunnelDaily(days = 7): Promise<MetaFunnelRow[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase.rpc('get_meta_funnel_stats', { p_days: days });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    eventName: String(r.event_name ?? ''),
    day: String(r.day ?? ''),
    total: Number(r.total ?? 0),
    totalValue: Number(r.total_value ?? 0),
  }));
}